import { type FormEvent, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import LegalConsentCheckbox from "../components/auth/LegalConsentCheckbox";
import { api } from "../lib/api";
import { AppError } from "../lib/errors";
import {
  clearPendingLegalConsent,
  readPendingLegalConsent,
  savePendingLegalConsent,
} from "../lib/legalConsent";
import { UI_TEXT_TONE } from "../lib/uiTone";

export const EMAIL_OTP_PENDING_KEY = "wizmate.otp.pendingEmail";
export const EMAIL_OTP_MODE_KEY = "wizmate.otp.mode";

export default function EmailOtpEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode");
  const mode: "login" | "register" = rawMode === "register" ? "register" : "login";
  const [hadPendingLegalConsent] = useState(
    () => mode === "register" && readPendingLegalConsent() !== null
  );
  const [email, setEmail] = useState("");
  const [legalConsentAccepted, setLegalConsentAccepted] = useState(
    () => mode !== "register" || hadPendingLegalConsent
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittedRef = useRef(false);

  const trimmedEmail = email.trim();
  const canSubmit =
    trimmedEmail.length > 0 && !loading && (mode !== "register" || legalConsentAccepted);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submittedRef.current) return;

    setLoading(true);
    setError(null);
    submittedRef.current = true;

    try {
      await api.emailOtpRequest({ email: trimmedEmail });
      sessionStorage.setItem(EMAIL_OTP_PENDING_KEY, trimmedEmail);
      sessionStorage.setItem(EMAIL_OTP_MODE_KEY, mode);
      if (mode === "register") {
        savePendingLegalConsent();
      }
      navigate("/email-otp/verify");
    } catch (errorValue) {
      submittedRef.current = false;
      if (errorValue instanceof AppError) {
        if (errorValue.meta.status === 429) {
          setError("しばらく時間をおいてからお試しください。");
        } else if (errorValue.meta.status === 422 || errorValue.code === "BAD_REQUEST") {
          setError("メールアドレスの形式が正しくありません。");
        } else {
          setError("送信に失敗しました。時間をおいて再度お試しください。");
        }
      } else {
        setError("送信に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(244,227,215,0.9),_rgba(253,248,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center justify-center">
        <div className="w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-6 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div className="mb-6">
            <p className={`text-[11px] font-bold tracking-[0.12em] ${UI_TEXT_TONE.eyebrow}`}>SIGN IN / SIGN UP</p>
            <h1 className={`mt-2 text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>
              メールアドレスを入力してください
            </h1>
          </div>

          {error ? (
            <p className="mb-4 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">
              {error}
            </p>
          ) : null}

          <form onSubmit={(e) => void handleSubmit(e)}>
            <label htmlFor="email-otp-email-input" className="text-sm font-black text-[#5D4D46]">
              メールアドレス
            </label>
            <input
              id="email-otp-email-input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
              placeholder="example@example.com"
              autoFocus
              disabled={loading}
              autoComplete="email"
            />

            {mode === "register" && !hadPendingLegalConsent ? (
              <div className="mt-5">
                <LegalConsentCheckbox
                  id="email-otp-request-legal-consent"
                  checked={legalConsentAccepted}
                  returnTo="/email-otp/email?mode=register"
                  disabled={loading}
                  onChange={(checked) => {
                    setLegalConsentAccepted(checked);
                    setError(null);
                    if (checked) {
                      savePendingLegalConsent();
                    } else {
                      clearPendingLegalConsent();
                    }
                  }}
                />
              </div>
            ) : null}

            <button
              type="submit"
              disabled={!canSubmit}
              className="mt-5 w-full rounded-[1.6rem] bg-[#5D4D46] px-4 py-4 text-sm font-black text-[#FDF8F5] shadow-lg transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "送信中..." : "確認コードを送る"}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
